import React, { useEffect, useMemo, useRef, useState } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";

type ViewerProps = {
  fileBlob: Blob;
  fileName: string;
  onPdfPageChange?: (page: number, total: number) => void;
  scrollToPage?: { page: number; total: number } | null;
};

const isTxt = (name: string) => name.toLowerCase().endsWith(".txt");

const Viewer = React.memo(
  ({ fileBlob, fileName, onPdfPageChange, scrollToPage }: ViewerProps) => {
    const [url, setUrl] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [highlight, setHighlight] = useState<{
      start: number;
      end: number;
    } | null>(null);
    const textContainerRef = useRef<HTMLPreElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const matchRef = useRef<HTMLSpanElement | null>(null);
    const lastPageRef = useRef<{ page: number; total: number } | null>(null);

    const txt = useMemo(() => isTxt(fileName), [fileName]);

    useEffect(() => {
      lastPageRef.current = null;
    }, [fileBlob, fileName]);

    useEffect(() => {
      // Clean previous state
      setUrl("");
      setText("");
      setHighlight(null);

      if (txt) {
        // Read as text and render ourselves
        fileBlob
          .text()
          .then(setText)
          .catch(() => setText("Failed to load text file."));
        return;
      }

      // For non-text, use object URL + DocViewer
      const objectUrl = URL.createObjectURL(fileBlob);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }, [fileBlob, txt]);

    useEffect(() => {
      if (!txt || !scrollToPage || !text) {
        setHighlight(null);
        return;
      }

      const pattern = new RegExp(
        `\\bPAGE\\s*:\\s*${scrollToPage.page}\\s*/\\s*${scrollToPage.total}\\b`,
        "i"
      );
      const match = pattern.exec(text);
      if (!match || match.index === undefined) {
        setHighlight(null);
        return;
      }

      setHighlight({ start: match.index, end: match.index + match[0].length });
    }, [scrollToPage, text, txt]);

    useEffect(() => {
      if (!txt || !highlight) return;
      const container = textContainerRef.current;
      const match = matchRef.current;
      if (!container || !match) return;

      const containerRect = container.getBoundingClientRect();
      const matchRect = match.getBoundingClientRect();
      const delta = matchRect.top - containerRect.top;
      const target = delta;
      const nextTop = Math.max(
        0,
        Math.min(container.scrollHeight - container.clientHeight, container.scrollTop + target)
      );
      container.scrollTop = nextTop;
    }, [highlight, txt]);

    useEffect(() => {
      if (txt || !onPdfPageChange) return;
      const container = containerRef.current;
      if (!container) return;

      const extractPageInfo = (value: string) => {
        const match = value.match(/(\d+)\s*\/\s*(\d+)/);
        if (!match) return null;
        const page = Number.parseInt(match[1], 10);
        const total = Number.parseInt(match[2], 10);
        if (!Number.isFinite(page) || !Number.isFinite(total)) return null;
        return { page, total };
      };

      const readPageInfo = () => {
        const infoElement =
          container.querySelector("#pdf-pagination-info") ||
          container.querySelector("#pdf-page-info");
        if (!infoElement || !infoElement.textContent) return;
        const nextInfo = extractPageInfo(infoElement.textContent);
        if (!nextInfo) return;
        const last = lastPageRef.current;
        if (last && last.page === nextInfo.page && last.total === nextInfo.total) {
          return;
        }
        lastPageRef.current = nextInfo;
        onPdfPageChange(nextInfo.page, nextInfo.total);
      };

      readPageInfo();

      const observer = new MutationObserver(readPageInfo);
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      return () => observer.disconnect();
    }, [onPdfPageChange, txt]);

    if (txt) {
      if (!text) return null;
      return (
      <pre
        ref={textContainerRef}
        style={{
          width: "100%",
            height: "100vh",
            margin: 0,
            padding: 12,
            overflow: "auto",
            boxSizing: "border-box",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {highlight ? (
            <>
              {text.slice(0, highlight.start)}
              <span ref={matchRef}>{text.slice(highlight.start, highlight.end)}</span>
              {text.slice(highlight.end)}
            </>
          ) : (
            text
          )}
        </pre>
      );
    }

    if (!url) return null;

    return (
      <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
        <DocViewer
          style={{ width: "100%", height: "100%" }}
          pluginRenderers={DocViewerRenderers}
          documents={[{ uri: url, fileName }]}
          config={{
            header: { disableFileName: true, disableHeader: true },
            pdfZoom: { defaultZoom: 0.5, zoomJump: 0.1 },
          }}
        />
      </div>
    );
  },
  (prev, next) =>
    prev.fileBlob === next.fileBlob &&
    prev.fileName === next.fileName &&
    prev.onPdfPageChange === next.onPdfPageChange &&
    prev.scrollToPage?.page === next.scrollToPage?.page &&
    prev.scrollToPage?.total === next.scrollToPage?.total
);

export default Viewer;
